import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Flame, Scale, ClipboardCheck, UtensilsCrossed, ChevronRight, Trophy } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConsistencyStreak({ refreshTrigger }) {
  const navigate = useNavigate();
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchStreakData();
  }, [refreshTrigger]);

  const fetchStreakData = async () => {
    try {
      const response = await axios.get(`${API}/consistency-streak`);
      setStreakData(response.data);
    } catch (error) {
      console.error('Error fetching streak data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTotalStreak = () => {
    if (!streakData) return 0;
    // Return the recipe logging streak as the main streak (since that's the most important one)
    return streakData.recipe_logging?.current_streak || 0;
  };

  const getStreakMessage = () => {
    const streak = getTotalStreak();
    if (streak === 0) return "Start logging meals to build your streak!";
    if (streak === 1) return "1 day of consistency! Keep going! 💪";
    if (streak < 7) return `${streak} days of consistency! You're building a habit! 🔥`;
    if (streak < 14) return `${streak} days strong! You're crushing it! 🚀`;
    if (streak < 30) return `${streak} days! Incredible dedication! ⭐`;
    return `${streak} days! You're unstoppable! 🏆`;
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200 animate-pulse">
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-orange-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-5 bg-orange-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-orange-100 rounded w-2/3"></div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Simple Streak Card - Click to see details */}
      <Card 
        className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200 cursor-pointer hover:shadow-md transition-all hover:border-orange-300"
        onClick={() => setShowDetails(true)}
        data-testid="consistency-streak-card"
      >
        <CardContent className="p-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {getTotalStreak()}
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 text-lg mb-1">Consistency Streak</h3>
              <p className="text-gray-700">{getStreakMessage()}</p>
            </div>
            <ChevronRight className="h-5 w-5 text-gray-400" />
          </div>
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Flame className="h-6 w-6 text-orange-500" />
              Your Consistency Streaks
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Today's Meals Status */}
            {streakData?.recipe_logging && (
              <div className={`rounded-lg p-4 ${
                streakData.recipe_logging.today_complete 
                  ? 'bg-green-50 border border-green-200' 
                  : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900 flex items-center gap-2">
                    <UtensilsCrossed className="h-4 w-4 text-orange-500" />
                    Today&apos;s Meals
                  </span>
                  {streakData.recipe_logging.today_complete && (
                    <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full">
                      ✓ Complete
                    </span>
                  )}
                </div>
                
                {streakData.recipe_logging.today_complete ? (
                  <p className="text-green-700 text-sm">
                    🎉 You&apos;ve logged all your meals today!
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {streakData.recipe_logging.required_meals?.map(meal => {
                      const isLogged = streakData.recipe_logging.today_logged?.includes(meal);
                      return (
                        <span 
                          key={meal}
                          className={`text-xs px-2 py-1 rounded-full ${
                            isLogged 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-200 text-gray-600'
                          }`}
                        >
                          {isLogged ? '✓' : '○'} {meal}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Streak Stats */}
            <div className="grid grid-cols-3 gap-3">
              {/* Weight Log */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <Scale className="h-6 w-6 text-blue-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-600">
                  {streakData?.weight_log?.current_streak || 0}
                </p>
                <p className="text-xs text-gray-600">days</p>
                <p className="text-xs text-gray-500 mt-1">Weight Log</p>
                {streakData?.weight_log?.best_streak > 0 && (
                  <p className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1">
                    <Trophy className="h-3 w-3" /> Best: {streakData.weight_log.best_streak}
                  </p>
                )}
              </div>

              {/* Check-ins */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                <ClipboardCheck className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-purple-600">
                  {streakData?.weekly_checkin?.current_streak || 0}
                </p>
                <p className="text-xs text-gray-600">weeks</p>
                <p className="text-xs text-gray-500 mt-1">Check-ins</p>
                {streakData?.weekly_checkin?.best_streak > 0 && (
                  <p className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1">
                    <Trophy className="h-3 w-3" /> Best: {streakData.weekly_checkin.best_streak}
                  </p>
                )}
              </div>

              {/* Meal Logging */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                <UtensilsCrossed className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-orange-600">
                  {streakData?.recipe_logging?.current_streak || 0}
                </p>
                <p className="text-xs text-gray-600">days</p>
                <p className="text-xs text-gray-500 mt-1">Meal Logging</p>
                {streakData?.recipe_logging?.best_streak > 0 && (
                  <p className="text-xs text-gray-400 mt-1 flex items-center justify-center gap-1">
                    <Trophy className="h-3 w-3" /> Best: {streakData.recipe_logging.best_streak}
                  </p>
                )}
              </div>
            </div>

            {/* Go to Meal Plan button */}
            <button
              onClick={() => {
                setShowDetails(false);
                navigate('meal-plan');
              }}
              className="w-full bg-[#1a61e8] hover:bg-[#1552c7] text-white py-3 rounded-lg font-medium transition-colors"
            >
              Go to Meal Plan →
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
